/** Date helpers on ISO yyyy-MM-dd strings (no time zones involved). */
const pad = (n: number) => String(n).padStart(2, '0');

export const toIso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const todayIso = () => toIso(new Date());

export function fromIso(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getMonth() === Number(m[2]) - 1 ? d : null;
}

/** dd/MM/yyyy (document 05). */
export function displayDate(iso: string): string {
  const d = fromIso(iso);
  return d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : '';
}

/** Accepts d/m/yyyy, d-m-yy, d.m.yyyy, ddmmyyyy or yyyy-mm-dd; returns ISO or null. */
export function parseDisplayDate(text: string): string | null {
  const s = text.trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return fromIso(s) ? s : null;
  const found = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(s);
  const m: string[] | null = found
    ? [...found]
    : /^\d{8}$/.test(s)
      ? [s, s.slice(0, 2), s.slice(2, 4), s.slice(4)]
      : null;
  if (!m) return null;
  const year = m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  const iso = `${year}-${pad(Number(m[2]))}-${pad(Number(m[1]))}`;
  return fromIso(iso) ? iso : null;
}

/** QBO date-field shortcuts (document 05): T + - W K M H Y R. Returns null for other keys. */
export function applyDateShortcut(key: string, iso: string): string | null {
  const base = fromIso(iso) ?? new Date();
  const y = base.getFullYear();
  const mo = base.getMonth();
  const day = base.getDay(); // 0 = Sunday
  switch (key.toLowerCase()) {
    case 't':
      return todayIso();
    case '+':
    case '=':
      return toIso(new Date(y, mo, base.getDate() + 1));
    case '-':
      return toIso(new Date(y, mo, base.getDate() - 1));
    case 'w':
      return toIso(new Date(y, mo, base.getDate() - day));
    case 'k':
      return toIso(new Date(y, mo, base.getDate() + (6 - day)));
    case 'm':
      return toIso(new Date(y, mo, 1));
    case 'h':
      return toIso(new Date(y, mo + 1, 0));
    case 'y':
      return toIso(new Date(y, 0, 1));
    case 'r':
      return toIso(new Date(y, 11, 31));
    default:
      return null;
  }
}

/** List date filters (document 05 list page: preset ranges). */
export const PERIODS: Array<[string, string]> = [
  ['all', 'All dates'],
  ['30', 'Last 30 days'],
  ['month', 'This month'],
  ['quarter', 'This quarter'],
  ['year', 'This year'],
  ['365', 'Last 12 months'],
];

export function periodStart(p: string): string | null {
  const d = new Date();
  const iso = (x: Date) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  switch (p) {
    case '30':
      return iso(new Date(d.getFullYear(), d.getMonth(), d.getDate() - 30));
    case '365':
      return iso(new Date(d.getFullYear() - 1, d.getMonth(), d.getDate()));
    case 'month':
      return iso(new Date(d.getFullYear(), d.getMonth(), 1));
    case 'quarter':
      return iso(new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1));
    case 'year':
      return iso(new Date(d.getFullYear(), 0, 1));
    default:
      return null;
  }
}

/**
 * The UTC offset of an IANA time zone today, as `UTC+05:30` (Asia/Kolkata) or `UTC+04:00`
 * (Asia/Dubai); the zone's name alone when the browser does not know it.
 */
export function utcOffsetLabel(timeZone: string, at: Date = new Date()): string | null {
  try {
    const part = new Intl.DateTimeFormat('en-GB', { timeZone, timeZoneName: 'longOffset' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName')?.value;
    if (!part) return null;
    return part === 'GMT' ? 'UTC+00:00' : part.replace(/^GMT/, 'UTC');
  } catch {
    return null;
  }
}
